import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { profile as profileApi } from "@/lib/api";

/**
 * SettingsCard — white card wrapper with an icon + heading + optional
 * help copy. The Settings page composes ~half a dozen of these; keep the
 * shape identical so vertical rhythm stays consistent.
 */
export const SettingsCard = ({ icon, title, help, children }) => (
  <div className="bg-white rounded-3xl border border-[#E8E5DF] p-5">
    <div className="flex items-center gap-2 text-leaf">
      {icon}
      <h2 className="text-xs uppercase tracking-[0.18em] font-medium">{title}</h2>
    </div>
    {help && <p className="mt-2 text-sm text-leaf-mist">{help}</p>}
    <div className="mt-4">{children}</div>
  </div>
);

/**
 * ChoiceButton — pill-shaped radio used inside SettingsCards (language,
 * text-size). Renders as a real <button role="radio"> so keyboard users
 * get standard radio semantics.
 */
export const ChoiceButton = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    role="radio"
    aria-checked={active}
    data-testid={testId}
    onClick={onClick}
    className={`rounded-full py-2.5 border transition font-medium ${
      active
        ? "bg-leaf text-white border-leaf"
        : "bg-white text-leaf-ink border-[#E8E5DF] hover:bg-parchment"
    }`}
  >
    {children}
  </button>
);

/**
 * SettingsLink — chevron list-row used inside the Info-links card. The
 * MaintenanceSection uses these plus a handful of <button>s with the same
 * shape.
 */
export const SettingsLink = ({ to, icon, children, testId }) => (
  <Link to={to} data-testid={testId} className="flex items-center gap-3 px-5 py-4 hover:bg-parchment transition text-leaf-ink">
    <span className="text-leaf">{icon}</span>
    <span className="flex-1">{children}</span>
    <span className="text-leaf-mist">›</span>
  </Link>
);

/**
 * useSaveProfile — shared PATCH /profile helper. Every section that
 * mutates a user preference calls this so we get one toast pattern +
 * one AuthContext update path.
 */
export function useSaveProfile() {
  const { setUser } = useAuth();
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  const saveProfile = async (payload, successMsg) => {
    setSaving(true);
    try {
      const { data } = await profileApi.update(payload);
      setUser(data);
      if (successMsg) toast.success(successMsg);
      return data;
    } catch {
      toast.error(t("settings.stateError"));
      return null;
    } finally {
      setSaving(false);
    }
  };

  return { saveProfile, saving };
}
